import type { FC } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { getStatWinner } from 'app/utils/entities/match_data';
import IconWithTooltip from 'app/components/IconWithTooltip';
import Tooltip from 'app/components/Tooltip';
import StatValue from 'app/components/StatValue';
import StatWinnerArrow from 'app/components/StatWinnerArrow';
import PicksAndBans from 'app/components/Blocks/TeamsStatsBlock/PicksAndBans';

import { getDota2TeamStatsLines } from './utils';
import style from './style.module.scss';

interface Props {
  data: TeamsStatsBlockMatch;
}

const Dota2TeamStats: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const lines = getDota2TeamStatsLines(data);
  const opponent1Stat = data.opponents.at(0)?.stats as StatsDota2 | undefined;
  const opponent2Stat = data.opponents.at(1)?.stats as StatsDota2 | undefined;

  return (
    <div className={style.teamsStats}>
      <ul>
        {lines.map(line => {
          const key = `blocks_teams-stats_videogame_${data.videogame_slug}_${line.name}`;
          const [isTeam1StatWinner, isTeam2StatWinner] = getStatWinner(line);

          return (
            <li key={key} className={style.statLine}>
              <div
                className={classNames(style.lineValue, {
                  [style.lineValueWinner]: isTeam1StatWinner,
                  [style.lineValueLoser]: isTeam2StatWinner
                })}
              >
                <StatValue value={line.team1Value} displayFunction={line.displayFunction} />
              </div>
              <div>{isTeam1StatWinner && <StatWinnerArrow direction="left" number={4} />}</div>
              {line.Icon ? (
                <IconWithTooltip label={t(`${key}_tooltip`)} Icon={line.Icon} />
              ) : (
                <Tooltip content={t(`${key}_tooltip`)}>{t(`${key}_title`)}</Tooltip>
              )}
              <div>{isTeam2StatWinner && <StatWinnerArrow direction="right" number={4} />}</div>
              <div
                className={classNames(style.lineValue, {
                  [style.lineValueWinner]: isTeam2StatWinner,
                  [style.lineValueLoser]: isTeam1StatWinner
                })}
              >
                <StatValue value={line.team2Value} displayFunction={line.displayFunction} />
              </div>
            </li>
          );
        })}
      </ul>

      <PicksAndBans
        opponent1PicksAndbans={opponent1Stat}
        opponent2PicksAndbans={opponent2Stat}
        videogame="dota2"
      />
    </div>
  );
};

Dota2TeamStats.displayName = 'Dota2TeamStats';

export default Dota2TeamStats;
